export interface ThemePreset {
  key: string;
  name: string;
  primary: string;
  primaryDark: string;
  primaryLight: string;
  primarySurface: string;
  borderAccent: string;
  borderAccentAlpha: string;
  headerBg: string;
  footerBg: string;
  navGradient: string;
  focusRing: string;
  description: string;
}

export const THEME_PRESETS_REGISTRY: Record<string, ThemePreset> = {
  wafi_crimson: {
    key: 'wafi_crimson',
    name: 'WaFi Crimson Royalty',
    primary: '#7A0C0C',
    primaryDark: '#580808',
    primaryLight: '#9E1B1B',
    primarySurface: '#FDF2F2',
    borderAccent: '#EAD5D5',
    borderAccentAlpha: 'rgba(122, 12, 12, 0.14)',
    headerBg: '#FDF2F2',
    footerBg: '#FDF2F2',
    navGradient: 'linear-gradient(90deg, #6B0A0A 0%, #7A0C0C 45%, #580808 100%)',
    focusRing: 'rgba(122, 12, 12, 0.2)',
    description: 'Rich maroon crimson accent with soft pearl surface tint and deep velvet navigation bar.'
  },
  classic_emerald: {
    key: 'classic_emerald',
    name: 'Eco Emerald Green',
    primary: '#10B981',
    primaryDark: '#059669',
    primaryLight: '#34D399',
    primarySurface: '#ECFDF5',
    borderAccent: '#A7F3D0',
    borderAccentAlpha: 'rgba(16, 185, 129, 0.16)',
    headerBg: '#ECFDF5',
    footerBg: '#ECFDF5',
    navGradient: 'linear-gradient(90deg, #047857 0%, #10B981 45%, #059669 100%)',
    focusRing: 'rgba(16, 185, 129, 0.2)',
    description: 'Fresh eco emerald green theme with clean light surface tint.'
  },
  royal_sapphire: {
    key: 'royal_sapphire',
    name: 'Royal Sapphire Blue',
    primary: '#1E40AF',
    primaryDark: '#1E3A8A',
    primaryLight: '#3B82F6',
    primarySurface: '#EFF6FF',
    borderAccent: '#BFDBFE',
    borderAccentAlpha: 'rgba(30, 64, 175, 0.16)',
    headerBg: '#EFF6FF',
    footerBg: '#EFF6FF',
    navGradient: 'linear-gradient(90deg, #172554 0%, #1E40AF 45%, #1E3A8A 100%)',
    focusRing: 'rgba(30, 64, 175, 0.2)',
    description: 'Luxurious sapphire navy blue theme for an executive corporate aesthetic.'
  },
  midnight_obsidian: {
    key: 'midnight_obsidian',
    name: 'Luxury Midnight Obsidian',
    primary: '#18181B',
    primaryDark: '#09090B',
    primaryLight: '#3F3F46',
    primarySurface: '#F4F4F5',
    borderAccent: '#E4E4E7',
    borderAccentAlpha: 'rgba(24, 24, 27, 0.14)',
    headerBg: '#F4F4F5',
    footerBg: '#F4F4F5',
    navGradient: 'linear-gradient(90deg, #09090B 0%, #18181B 45%, #27272A 100%)',
    focusRing: 'rgba(24, 24, 27, 0.2)',
    description: 'Sleek luxury black obsidian aesthetic with crisp monochrome contrasts.'
  },
  velvet_amethyst: {
    key: 'velvet_amethyst',
    name: 'Velvet Amethyst Purple',
    primary: '#6D28D9',
    primaryDark: '#5B21B6',
    primaryLight: '#8B5CF6',
    primarySurface: '#F5F3FF',
    borderAccent: '#DDD6FE',
    borderAccentAlpha: 'rgba(109, 40, 217, 0.16)',
    headerBg: '#F5F3FF',
    footerBg: '#F5F3FF',
    navGradient: 'linear-gradient(90deg, #4C1D95 0%, #6D28D9 45%, #5B21B6 100%)',
    focusRing: 'rgba(109, 40, 217, 0.2)',
    description: 'Vibrant imperial amethyst purple theme with premium lavender highlights.'
  }
};

export const applyThemePreset = (presetKey: string): void => {
  const theme = THEME_PRESETS_REGISTRY[presetKey] || THEME_PRESETS_REGISTRY.wafi_crimson;
  const root = document.documentElement;

  // Set core CSS Custom Properties dynamically onto :root
  root.style.setProperty('--primary', theme.primary);
  root.style.setProperty('--primary-dark', theme.primaryDark);
  root.style.setProperty('--primary-light', theme.primaryLight);
  root.style.setProperty('--primary-surface', theme.primarySurface);
  root.style.setProperty('--border-accent', theme.borderAccent);
  root.style.setProperty('--border-accent-alpha', theme.borderAccentAlpha);
  root.style.setProperty('--header-bg', theme.headerBg);
  root.style.setProperty('--footer-bg', theme.footerBg);
  root.style.setProperty('--nav-gradient', theme.navGradient);
  root.style.setProperty('--focus-ring', theme.focusRing);

  // Directly update elements that rely on inline styles or specific background overrides
  const navElement = document.querySelector('.desktop-navigation-menu') as HTMLElement;
  if (navElement) {
    navElement.style.background = theme.navGradient;
  }

  const headerMainElement = document.querySelector('.header-main') as HTMLElement;
  if (headerMainElement) {
    headerMainElement.style.background = theme.headerBg;
  }

  const footerElement = document.querySelector('.footer') as HTMLElement;
  if (footerElement) {
    footerElement.style.background = theme.footerBg;
  }
};
